import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { useT, hasValue, EComponentBaseType } from '@progress-fe/core';
import { Box, Center, Menu, Flex, Grid, Button, GridItem, IconButton, MenuButton, MenuDivider, MenuItem, MenuList } from '@chakra-ui/react';
import { Dialog, Svg } from '../../base';
import { ComponentsLists } from '../../lists';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldError, JsFieldInspector } from '../../jsFormBase';
/**
 * Component renders "/schemas/jsf-components-list" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TComponentsListFormData.
 */
const ComponentsListJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(false);
    const context = useContext(JsFormConfigContext) ?? {};
    const { components, addToFavorites, removeFromFavorites, isInspector } = context;
    const { favorites = [], pureList = [], pseudoList = [], dictionary = {} } = components ?? {};
    const { t } = useT();
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig ?? null,
        values: formData?.fieldValues ?? null,
        onChange: props.onChange
    });
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const getComponentNameByUuid = (uuid) => {
        return dictionary[uuid]?.name ?? pseudoList.find((i) => i.uuid === uuid)?.name ?? '';
    };
    const isFieldInvalid = !!props.rawErrors;
    const hasComponents = !!fieldValues?.length;
    const onChangeCompositionList = (items) => {
        if (fieldValues) {
            onChangeFieldValues(items);
        }
    };
    const handleAddComponent = (key) => {
        if (fieldValues) {
            onChangeCompositionList([...fieldValues, key]);
        }
    };
    const handleDeleteComponent = (key) => {
        if (fieldValues) {
            const newList = fieldValues.filter((item) => item !== key);
            onChangeCompositionList(newList);
        }
    };
    const handleClearValues = () => {
        onChangeCompositionList([]);
    };
    const tabs = useMemo(() => {
        return [
            {
                type: null,
                label: t(`common.favorites`),
                components: favorites
            },
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            },
            {
                type: EComponentBaseType.HYPOTHETICAL,
                label: t(`enum.componentBaseType.${EComponentBaseType.HYPOTHETICAL}`),
                components: []
            },
            {
                type: EComponentBaseType.PSEUDO,
                label: t(`enum.componentBaseType.${EComponentBaseType.PSEUDO}`),
                components: pseudoList
            }
        ];
    }, [favorites, pureList, pseudoList, t]);
    if (!fieldConfig) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { "data-testid": "ComponentsListJsField-test", "data-name": props.name, children: [_jsxs(Flex, { p: "0 0 2px 0", flexDirection: "column", gap: "8px", children: [hasComponents ? (_jsxs(GridItem, { p: "4px", borderRadius: "4px", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', children: [_jsxs(Grid, { gap: "4px", p: "0 0 4px 0", alignItems: "center", borderBottom: "1px solid", borderColor: "border", templateColumns: "1fr 24px", children: [_jsx(GridItem, { children: t('fields.component') }), _jsx(GridItem, { children: _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghostTr", icon: _jsx(Svg, { size: "s12", name: "Cross" }), isDisabled: props.schema.readOnly ?? fieldConfig?.readOnly, onClick: () => handleClearValues() }) })] }), _jsx(Box, { p: "2px 0", children: fieldValues.map((uuid) => {
                                        return (_jsxs(Grid, { gap: "4px", p: "2px 0", templateColumns: "1fr 24px", children: [_jsx(Flex, { align: "center", className: 'cut-text', children: getComponentNameByUuid(uuid) }), _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghost", isDisabled: props.schema.readOnly ?? fieldConfig?.readOnly, icon: _jsx(Svg, { size: "s12", name: "Cross" }), onClick: () => handleDeleteComponent(uuid) })] }, uuid));
                                    }) })] })) : (_jsx(Center, { h: "32px", color: "bodyText", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', borderRadius: "4px", children: _jsx(Box, { opacity: "0.6", children: t('components.no') }) })), !props.schema.readOnly && !fieldConfig?.readOnly && (_jsx(GridItem, { children: _jsxs(Menu, { matchWidth: true, offset: [0, 2], variant: "outline", children: [_jsx(MenuButton, { size: "xs", as: Button, width: "100%", variant: "ghost", onClick: () => setIsDialog(false), isDisabled: !fieldValues, children: t('actions.add') }), _jsxs(MenuList, { motionProps: { animate: false }, zIndex: 2, children: [favorites.map((item) => (_jsx(MenuItem, { isDisabled: fieldValues?.includes(item.uuid), onClick: () => handleAddComponent(item.uuid), children: item.name }, item.uuid))), favorites.length === 0 && (_jsx(MenuItem, { isDisabled: true, children: t('components.noFavorite') })), _jsx(MenuDivider, {}), _jsx(MenuItem, { onClick: () => setIsDialog(true), children: t('components.all') })] })] }) }))] }), isDialog && (_jsx(Dialog, { title: t('components.add'), onClose: () => setIsDialog(false), children: _jsx(GridItem, { pt: "8px", children: _jsx(ComponentsLists, { tabs: tabs, tableSx: { height: 500 }, selectedComponentsIds: fieldValues ?? [], onAddComponent: handleAddComponent, onDeleteComponent: handleDeleteComponent, onAddToFavorite: (item) => addToFavorites?.([item]), onDeleteFromFavorite: (item) => removeFromFavorites?.([item]), onClose: () => setIsDialog(false) }) }) }))] }) }));
};
export const ComponentsListJsField = ComponentsListJsFieldFC;
//# sourceMappingURL=ComponentsListJsField.js.map